/**
 * HUD elements — v0M2.4a.
 *
 * Bottom-right compass rose (N/E/S/W markers),
 * bottom-left terrain legend swatches,
 * top-right zoom-step indicator with the active step highlighted.
 *
 * Kept intentionally small — a proper HUD frame lands with UI_UX.md
 * screens at v0M3+.
 */

import type { Camera } from './camera';
import type { LoadedMap } from './map-loader';
import { pxPerCell, ZOOM_STEPS } from './camera';
import { terrainColor, Terrain } from './terrain-palette';

const HUD_FONT = '11px "SF Mono", Menlo, Consolas, monospace';
const HUD_FG = '#e6dcc4';
const HUD_BG = 'rgba(14, 11, 6, 0.75)';
const HUD_STROKE = 'rgba(230, 220, 196, 0.4)';

// ---------------------------------------------------------------------------
// HUD label — map name + dimensions + zoom (v0M2.4 keeps this bottom-left)
// ---------------------------------------------------------------------------

export function drawMapLabel(
  ctx: CanvasRenderingContext2D,
  map: LoadedMap,
  camera: Camera,
  _canvasW: number,
  canvasH: number,
): void {
  ctx.save();
  ctx.font = HUD_FONT;
  ctx.fillStyle = HUD_FG;
  ctx.textBaseline = 'bottom';
  ctx.textAlign = 'left';
  ctx.fillText(
    `${map.name} · ${map.width}×${map.height} · ${map.provinces.length} provinces · zoom ${pxPerCell(camera).toFixed(1)} px/cell`,
    12,
    canvasH - 12,
  );
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Terrain legend
// ---------------------------------------------------------------------------

const LEGEND_ENTRIES: readonly { readonly id: number; readonly label: string }[] = [
  { id: Terrain.Plains, label: 'Plains' },
  { id: Terrain.Farmland, label: 'Farmland' },
  { id: Terrain.Forest, label: 'Forest' },
  { id: Terrain.Hills, label: 'Hills' },
  { id: Terrain.Mountains, label: 'Mountains' },
  { id: Terrain.Desert, label: 'Desert' },
  { id: Terrain.Swamp, label: 'Swamp' },
  { id: Terrain.Lake, label: 'Lake' },
  { id: Terrain.Sea, label: 'Sea' },
  // MFix12.2 (2026-07-27) — River (id 9) added at MFix9 but omitted from
  // the legend. Playtest via Chrome-view session surfaced the gap.
  { id: Terrain.River, label: 'River' },
];

export function drawLegend(
  ctx: CanvasRenderingContext2D,
  _canvasW: number,
  canvasH: number,
): void {
  const rowH = 14;
  const swatch = 10;
  const pad = 8;
  const boxW = 92;
  const boxH = LEGEND_ENTRIES.length * rowH + pad * 2;
  const x0 = 12;
  const y0 = canvasH - 32 - boxH;

  ctx.save();
  ctx.fillStyle = HUD_BG;
  ctx.strokeStyle = HUD_STROKE;
  ctx.lineWidth = 1;
  roundRect(ctx, x0, y0, boxW, boxH, 3);
  ctx.fill();
  ctx.stroke();

  ctx.font = HUD_FONT;
  ctx.fillStyle = HUD_FG;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  for (let i = 0; i < LEGEND_ENTRIES.length; i++) {
    const entry = LEGEND_ENTRIES[i]!;
    const [r, g, b] = terrainColor(entry.id);
    const rowY = y0 + pad + i * rowH + rowH / 2;
    ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    ctx.fillRect(x0 + pad, rowY - swatch / 2, swatch, swatch);
    ctx.strokeStyle = HUD_STROKE;
    ctx.strokeRect(x0 + pad + 0.5, rowY - swatch / 2 + 0.5, swatch - 1, swatch - 1);
    ctx.fillStyle = HUD_FG;
    ctx.fillText(entry.label, x0 + pad + swatch + 6, rowY);
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Zoom indicator (top-right)
// ---------------------------------------------------------------------------

export function drawZoomIndicator(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const cellW = 40;
  const rowH = 18;
  const pad = 8;
  const boxW = cellW + pad * 2;
  const boxH = ZOOM_STEPS.length * rowH + pad * 2;
  // MFix7.3 (2026-07-23) — reposition from top-right to bottom-right to
  // avoid overlap with the speed HUD (DOM overlay at top:16px right:16px).
  const x0 = canvasW - 12 - boxW;
  const y0 = canvasH - 12 - boxH;

  ctx.save();
  ctx.fillStyle = HUD_BG;
  ctx.strokeStyle = HUD_STROKE;
  ctx.lineWidth = 1;
  roundRect(ctx, x0, y0, boxW, boxH, 3);
  ctx.fill();
  ctx.stroke();

  ctx.font = HUD_FONT;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  for (let i = ZOOM_STEPS.length - 1; i >= 0; i--) {
    const rowY = y0 + pad + (ZOOM_STEPS.length - 1 - i) * rowH + rowH / 2;
    const isActive = i === camera.zoomStep;
    if (isActive) {
      ctx.fillStyle = HUD_FG;
      ctx.fillRect(x0 + pad, rowY - rowH / 2 + 2, cellW, rowH - 4);
      ctx.fillStyle = '#0e0b06';
    } else {
      ctx.fillStyle = HUD_FG;
    }
    const step = ZOOM_STEPS[i];
    ctx.fillText(step !== undefined ? `${step}×` : '?', x0 + pad + cellW / 2, rowY);
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Compass rose (bottom-right)
// ---------------------------------------------------------------------------

export function drawCompass(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
): void {
  const cx = canvasW - 34;
  const cy = canvasH - 34;
  const r = 20;

  ctx.save();
  ctx.fillStyle = HUD_BG;
  ctx.strokeStyle = HUD_STROKE;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // North point
  ctx.fillStyle = '#a94438';
  ctx.beginPath();
  ctx.moveTo(cx, cy - r + 3);
  ctx.lineTo(cx - 4, cy);
  ctx.lineTo(cx + 4, cy);
  ctx.closePath();
  ctx.fill();

  // South counterweight
  ctx.fillStyle = HUD_FG;
  ctx.beginPath();
  ctx.moveTo(cx, cy + r - 3);
  ctx.lineTo(cx - 3, cy);
  ctx.lineTo(cx + 3, cy);
  ctx.closePath();
  ctx.fill();

  ctx.font = '9px "SF Mono", Menlo, Consolas, monospace';
  ctx.fillStyle = HUD_FG;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('N', cx, cy - r - 6);
  ctx.fillText('S', cx, cy + r + 8);
  ctx.fillText('E', cx + r + 8, cy);
  ctx.fillText('W', cx - r - 8, cy);
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
